import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Menu,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getNotificationsApi,
  markAllNotificationsReadApi,
  markNotificationReadApi,
} from "../../../API/notifications";
import theme from "../../../theme/theme";
import { initWebPush } from "../../../utils/webPush";

const POLL_MS = 30000;

const isRead = (n) => n.isRead === 1 || n.isRead === true;

const NotificationsMenu = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const lastSeenId = useRef(Number(localStorage.getItem("lastNotifId") || 0));
  const firstLoad = useRef(true);
  const loadRef = useRef(() => {});
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  const markSeen = (id) => {
    if (id > lastSeenId.current) {
      lastSeenId.current = id;
      localStorage.setItem("lastNotifId", String(id));
    }
  };

  // Show a desktop/system notification for the newest unseen items. On the very
  // first load we only set the baseline (no popup) so opening the app doesn't
  // replay every existing notification.
  const notify = useCallback((list) => {
    const maxId = list.reduce((m, n) => Math.max(m, n.id), lastSeenId.current);
    if (firstLoad.current) {
      firstLoad.current = false;
      markSeen(maxId);
      return;
    }
    if ("Notification" in window && Notification.permission === "granted") {
      list
        .filter((n) => n.id > lastSeenId.current && !isRead(n))
        .slice(0, 3)
        .reverse()
        .forEach((n) => {
          try {
            // eslint-disable-next-line no-new
            new Notification(n.title || "GAIF Meeting Hub", {
              body: n.body || "",
            });
          } catch (_) {}
        });
    }
    markSeen(maxId);
  }, []);

  // Foreground FCM push (tab focused) — show it and refresh the badge/list.
  
  const handleForeground = useCallback((payload) => {
    const title =
      payload?.notification?.title || payload?.data?.title || "GAIF Meeting Hub";
    const body = payload?.notification?.body || payload?.data?.body || "";
    if ("Notification" in window && Notification.permission === "granted") {
      try {
        // eslint-disable-next-line no-new
        new Notification(title, { body });
      } catch (_) {}
    }
    const nid = Number(payload?.data?.notificationId || 0);
    if (nid) markSeen(nid);
    loadRef.current();
  }, []);

  const load = useCallback(
    async (showLoader = false) => {
      if (showLoader) setLoading(true);
      try {
        const res = await getNotificationsApi(1, 30);
        const data = res.data?.data ?? [];
        setItems(data);
        setUnread(res.data?.unreadCount ?? 0);
        notify(data);
      } catch (_) {
        // ignore transient errors during polling
      } finally {
        if (showLoader) setLoading(false);
      }
    },
    [notify]
  );

  useEffect(() => {
    loadRef.current = load;
  }, [load]);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    // Register for background web push (FCM) — best effort.
    initWebPush(handleForeground);
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load, handleForeground]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    // Requesting on a user gesture is the most reliable moment.
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    load(true);
  };
  const handleClose = () => setAnchorEl(null);

  const handleItemClick = async (n) => {
    if (!isRead(n)) {
      try {
        await markNotificationReadApi(n.id);
      } catch (_) {}
      setItems((prev) =>
        prev.map((x) => (x.id === n.id ? { ...x, isRead: 1 } : x))
      );
      setUnread((u) => Math.max(0, u - 1));
    }
    handleClose();
    // Meeting notifications open the related meeting on the hub.
    if (n.scheduleId) {
      navigate(`/?meeting=${n.scheduleId}`);
    }
  };

  const handleMarkAll = async () => {
    try {
      await markAllNotificationsReadApi();
    } catch (_) {}
    setItems((prev) => prev.map((x) => ({ ...x, isRead: 1 })));
    setUnread(0);
  };

  return (
    <>
      <IconButton onClick={handleOpen} sx={{ color: "#77a9da" }} size="large">
        <Badge badgeContent={unread} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: { width: 360, maxHeight: 480 } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
          }}
        >
          <Typography sx={{ fontWeight: 700, color: theme.palette.secondary.main }}>
            Notifications
          </Typography>
          {unread > 0 && (
            <Button size="small" onClick={handleMarkAll} sx={{ textTransform: "none" }}>
              Mark all read
            </Button>
          )}
        </Box>
        <Divider />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : items.length === 0 ? (
          <Typography
            sx={{ px: 2, py: 3, color: theme.palette.gray.main, textAlign: "center" }}
          >
            No notifications
          </Typography>
        ) : (
          <List sx={{ py: 0 }}>
            {items.map((n) => (
              <ListItemButton
                key={n.id}
                onClick={() => handleItemClick(n)}
                sx={{
                  alignItems: "flex-start",
                  backgroundColor: isRead(n)
                    ? "transparent"
                    : "rgba(119,169,218,0.08)",
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: isRead(n) ? 500 : 700,
                      color: theme.palette.secondary.main,
                    }}
                  >
                    {n.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: theme.palette.gray.main }}>
                    {n.body}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11, color: theme.palette.gray.main, mt: 0.5 }}
                  >
                    {new Date(n.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
      </Menu>
    </>
  );
};

export default NotificationsMenu;
